<template>
  <div class="mt-[15px] rounded-md border bg-gray-25 p-4">
    <div v-if="checking" class="flex h-[100px] items-center justify-center">
      <UISpin />
    </div>

    <!-- Voice has more prerequisites than any other channel; list what is missing rather than
         render a form that can never place a call. -->
    <div v-else-if="blockers.length" id="voice-ai-setup" class="space-y-2 py-2">
      <div class="text-[16px] font-semibold text-gray-900">
        {{ t('eventNotification.voiceAi.setupTitle') }}
      </div>
      <div class="text-[13px] text-gray-500">
        {{ t('eventNotification.voiceAi.setupDescription') }}
      </div>
      <ul class="ml-4 list-disc text-[14px] text-gray-700">
        <li v-for="blocker in blockers" :key="blocker">{{ blockerLabel(blocker) }}</li>
      </ul>
    </div>

    <template v-else>
      <div class="mb-5 flex items-center space-x-3">
        <UISwitch
          id="voice-ai-notification-switch"
          v-model:value="enabled"
          size="medium"
          :aria-label="t('alt_text.voice_ai_notification_switch')"
        />
        <span class="text-gray-600">{{ enabled ? t('eventNotification.enabled') : t('eventNotification.disabled') }}</span>
      </div>

      <UIForm :model="form" class="w-full space-y-4" :class="{ 'disabled-div': !enabled }">
        <UIFormItem :label="t('eventNotification.voiceAi.agentLabel')" :feedback="t('eventNotification.voiceAi.agentHelper')">
          <div class="flex w-full items-center gap-2">
            <UISelect
              id="voice-ai-agent-select"
              v-model:value="form.agentId"
              class="min-w-0 grow"
              :options="agentOptions"
              :placeholder="t('eventNotification.voiceAi.agentPlaceholder')"
              :filterable="true"
            />
            <UITooltip v-if="form.agentId" placement="top">
              <template #trigger>
                <UIButton
                  id="voice-ai-open-agent"
                  class="shrink-0"
                  :aria-label="t('eventNotification.voiceAi.openAgent')"
                  @click="openSelectedAgent"
                >
                  <LinkExternal01Icon class="h-4 w-4" />
                </UIButton>
              </template>
              {{ t('eventNotification.voiceAi.openAgent') }}
            </UITooltip>
            <UIButton
              v-if="canCreateAgent"
              id="voice-ai-create-agent"
              class="shrink-0"
              type="primary"
              :loading="creatingAgent"
              :disabled="creatingAgent"
              @click="createReminderAgent"
            >
              <PlusIcon class="mr-1 h-4 w-4" />
              {{ creatingAgent ? t('eventNotification.voiceAi.creatingAgent') : t('eventNotification.voiceAi.createAgent') }}
            </UIButton>
          </div>
        </UIFormItem>
        <p v-if="canCreateAgent" class="-mt-2 text-[12px] text-gray-500">{{ t('eventNotification.voiceAi.createAgentHelper') }}</p>

        <UIFormItem :label="t('eventNotification.voiceAi.fromNumberLabel')" :show-feedback="false">
          <UISelect
            id="voice-ai-from-number-select"
            v-model:value="form.fromNumber"
            :options="numberOptions"
            :placeholder="t('eventNotification.voiceAi.fromNumberPlaceholder')"
            :filterable="true"
          />
        </UIFormItem>

        <UIFormItem
          :label="t('eventNotification.voiceAi.timingLabel')"
          :feedback="offsetErrorMessage || timingHelper"
          :validation-status="offsetErrorMessage ? 'error' : undefined"
        >
          <div class="flex w-full items-center gap-3">
            <UISelect id="voice-ai-offset-value" v-model:value="offsetDays" class="w-36 shrink-0" :options="offsetOptions" />
            <span class="whitespace-nowrap text-[14px] text-gray-600">{{ t('eventNotification.voiceAi.beforeAppointment') }}</span>
          </div>
        </UIFormItem>
      </UIForm>

      <div v-if="!requirementsDismissed" id="voice-ai-requirements" class="mt-4 rounded-md border border-gray-200 bg-white text-[13px] text-gray-700">
        <div class="flex items-center gap-1 py-1.5 pl-3 pr-2">
          <button
            type="button"
            class="flex min-w-0 grow items-center gap-2 text-left"
            :aria-expanded="requirementsOpen"
            @click="requirementsOpen = !requirementsOpen"
          >
            <InfoCircleIcon class="h-4 w-4 shrink-0 text-gray-500" />
            <span class="grow truncate font-semibold text-gray-800">{{ t('eventNotification.voiceAi.requirements.title') }}</span>
            <ChevronDownIcon class="h-4 w-4 shrink-0 text-gray-500 transition-transform" :class="{ 'rotate-180': requirementsOpen }" />
          </button>
          <button
            id="voice-ai-requirements-dismiss"
            type="button"
            class="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
            :aria-label="t('eventNotification.voiceAi.requirements.dismiss')"
            @click="dismissRequirements"
          >
            <XCloseIcon class="h-4 w-4" />
          </button>
        </div>
        <ul v-if="requirementsOpen" class="list-disc space-y-1.5 border-t border-gray-100 py-2.5 pl-9 pr-4">
          <li>{{ t('eventNotification.voiceAi.requirements.booking') }}</li>
          <li>
            <i18n-t keypath="eventNotification.voiceAi.requirements.greeting" tag="span" scope="global">
              <template #start>
                <code class="rounded bg-gray-100 px-1 text-[12px]">{{ START_TIME_TOKEN }}</code>
              </template>
              <template #calendar>
                <code class="rounded bg-gray-100 px-1 text-[12px]">{{ CALENDAR_NAME_TOKEN }}</code>
              </template>
            </i18n-t>
          </li>
        </ul>
      </div>

      <div
        v-if="consentState !== 'accepted'"
        id="voice-ai-consent"
        class="mt-4 rounded-md border border-gray-200 bg-white px-4 py-3"
        :class="{ 'disabled-div': !enabled }"
      >
        <UICheckbox id="voice-ai-terms-checkbox" v-model:checked="form.termsAgreed">
          {{
            consentState === 'upgrade' ? t('eventNotification.voiceAi.consent.updatedLabel') : t('eventNotification.voiceAi.consent.label')
          }}
        </UICheckbox>
        <p class="ml-7 mt-0.5 text-[12px] text-gray-500">
          {{
            consentState === 'upgrade' ? t('eventNotification.voiceAi.consent.updatedNote') : t('eventNotification.voiceAi.consent.note')
          }}
        </p>
        <button
          id="voice-ai-terms-toggle"
          type="button"
          class="ml-7 mt-2 flex items-center gap-1 text-[12px] font-medium text-primary-700"
          :aria-expanded="termsOpen"
          @click="termsOpen = !termsOpen"
        >
          {{ t('eventNotification.voiceAi.consent.termsTitle') }}
          <ChevronDownIcon class="h-3.5 w-3.5 transition-transform" :class="{ 'rotate-180': termsOpen }" />
        </button>
        <div v-if="termsOpen" id="voice-ai-terms" class="ml-7 mt-2 text-[12px] text-gray-700">
          <p class="font-medium text-gray-800">{{ t('eventNotification.voiceAi.consent.termsIntro') }}</p>
          <ul class="mt-1 list-disc space-y-1 pl-4">
            <li>{{ t('eventNotification.voiceAi.consent.terms1') }}</li>
            <li>{{ t('eventNotification.voiceAi.consent.terms2') }}</li>
            <li>{{ t('eventNotification.voiceAi.consent.terms3') }}</li>
          </ul>
        </div>
      </div>

      <div id="voice-ai-guidelines" class="mt-4 rounded-md border border-gray-200 bg-white">
        <button
          type="button"
          class="flex w-full items-center gap-2 px-4 py-3 text-left"
          :aria-expanded="guidelinesOpen"
          @click="guidelinesOpen = !guidelinesOpen"
        >
          <InfoCircleIcon class="h-4 w-4 shrink-0 text-gray-500" />
          <span class="grow text-[13px] font-semibold text-gray-800">{{ t('eventNotification.voiceAi.guidelines.title') }}</span>
          <ChevronDownIcon class="h-4 w-4 shrink-0 text-gray-500 transition-transform" :class="{ 'rotate-180': guidelinesOpen }" />
        </button>
        <ul v-if="guidelinesOpen" class="list-disc space-y-1.5 border-t border-gray-100 py-3 pl-9 pr-4 text-[13px] text-gray-700">
          <template v-if="requirementsDismissed">
            <li>{{ t('eventNotification.voiceAi.requirements.booking') }}</li>
            <li>
              <i18n-t keypath="eventNotification.voiceAi.requirements.greeting" tag="span" scope="global">
                <template #start>
                  <code class="rounded bg-gray-100 px-1 text-[12px]">{{ START_TIME_TOKEN }}</code>
                </template>
                <template #calendar>
                  <code class="rounded bg-gray-100 px-1 text-[12px]">{{ CALENDAR_NAME_TOKEN }}</code>
                </template>
              </i18n-t>
            </li>
          </template>
          <li>{{ t('eventNotification.voiceAi.guidelines.window') }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.oncePerDay') }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.queued') }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.skips') }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.country') }}</li>
          <li v-if="fallbackChannel">{{ t('eventNotification.voiceAi.guidelines.fallback', { channel: fallbackChannel }) }}</li>
          <li>{{ t('eventNotification.voiceAi.guidelines.capabilities') }}</li>
        </ul>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { EventNotificationService } from '@/class/services/EventNotificationService'
    import { VoiceAiReminderService } from '@/class/services/VoiceAiReminderService'
  import AppState from '@/states/app'
  import {
    eventNotificationState as state,
    getEventNotificationState,
    isVirtualNotification,
    stripNotificationIdForCreate,
  } from '@/states/EventNotificationState'
  import { AltType, NotificationChannel, NotificationConfig, NotificationType, ReceiverType, Schedule } from '@/types/notification'
  import {
    DEFAULT_VOICE_REMINDER_OFFSET,
    getCallingWindowAdvice,
    getVoiceAgentUrl,
    getVoiceReminderDayOptions,
    getVoiceReminderOffsetError,
    VOICE_AI_REQUIREMENTS_DISMISSED_KEY,
  } from '@/utils/voiceAiReminder'
  import {
    UIButton,
    UICheckbox,
    UIForm,
    UIFormItem,
    UISelect,
    UISpin,
    UISwitch,
    UITooltip,
    useNotification,
  } from '@gohighlevel/ghl-ui'
  import { ChevronDownIcon, InfoCircleIcon, LinkExternal01Icon, PlusIcon, XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'
  import { computed, onMounted, reactive, ref } from 'vue'
  import { useI18n } from 'vue-i18n'

  type Blocker = 'no-agent' | 'no-number' | 'not-enabled'
  type ConsentState = 'accepted' | 'required' | 'upgrade'

  const props = defineProps({
    calendarId: { type: String, required: true },
  })

  const emit = defineEmits(['update-notification'])
  const { t } = useI18n()

  const checking = ref(true)
  const blockers = ref<Blocker[]>([])
  const agentOptions = ref<{ label: string; value: string; disabled?: boolean }[]>([])
  const numberOptions = ref<{ label: string; value: string }[]>([])
  const consentState = ref<ConsentState>('required')
  const enabled = ref(false)
  const rowId = ref('')
  const guidelinesOpen = ref(false)
  const termsOpen = ref(false)
  const requirementsOpen = ref(true)
  const requirementsDismissed = ref(readRequirementsDismissed())
  // Merge-field tokens are the same in every language; kept out of the template so Vue does not parse the braces.
  // eslint-disable-next-line @platform-ui/eslint-custom-rules/no-untranslated-template-properties
  const START_TIME_TOKEN = '{{appointment.only_start_time}}'
  // eslint-disable-next-line @platform-ui/eslint-custom-rules/no-untranslated-template-properties
  const CALENDAR_NAME_TOKEN = '{{calendar.name}}'
  const creatingAgent = ref(false)
  // Hidden after Voice AI refuses a create (no agent write permission); there is no cheaper way to know up front.
  const createAgentForbidden = ref(false)
  const canCreateAgent = computed(() => !createAgentForbidden.value)
  const notification = useNotification()

  const calendarName = computed(() => {
    const calendars = (AppState.globalCalendars as { calendars?: Array<{ id: string; name?: string }> })?.calendars || []
    return calendars.find(calendar => calendar.id === props.calendarId)?.name || ''
  })

  const form = reactive({
    agentId: '',
    fromNumber: '',
    termsAgreed: false,
    interval: { ...DEFAULT_VOICE_REMINDER_OFFSET } as Schedule,
  })

  const offsetOptions = computed(() =>
    getVoiceReminderDayOptions().map(days => ({
      label: `${days} ${days === 1 ? t('eventNotification.voiceAi.units.day') : t('eventNotification.voiceAi.units.days')}`,
      value: days,
    }))
  )

  // A row saved with another unit (older staging builds) shows as unset until a day count is picked.
  const offsetDays = computed({
    get: () => (form.interval.unit === 'days' && form.interval.timeOffset ? form.interval.timeOffset : null),
    set: (days: number | null) => {
      form.interval = { timeOffset: days ?? 0, unit: 'days' }
    },
  })

  const offsetErrorMessage = computed(() => {
    const error = getVoiceReminderOffsetError(form.interval)
    if (!error) return ''
    return t('eventNotification.voiceAi.timingRangeError', {
      min: error.min,
      max: error.max,
      unit: t('eventNotification.voiceAi.units.days').toLowerCase(),
    })
  })

  function readRequirementsDismissed(): boolean {
    try {
      return window.localStorage.getItem(VOICE_AI_REQUIREMENTS_DISMISSED_KEY) === 'true'
    } catch {
      return false
    }
  }

  function dismissRequirements() {
    requirementsDismissed.value = true
    try {
      window.localStorage.setItem(VOICE_AI_REQUIREMENTS_DISMISSED_KEY, 'true')
    } catch {
      // Private browsing: the strip simply comes back next time.
    }
  }

  const timingHelper = computed(() => {
    const advice = getCallingWindowAdvice(form.interval)
    return t('eventNotification.voiceAi.timingHelper', { from: advice.safeFromLabel, until: advice.safeUntilLabel })
  })

  // When the call can't be placed, the reminder falls back to a channel the admin already enabled.
  const fallbackChannel = computed(() => {
    if (AppState.isSmsNotificationsEnabled && getEventNotificationState('sms')) return t('eventNotification.sms.channelName')
    if (getEventNotificationState('email')) return t('eventNotification.email.channelName')
    if (getEventNotificationState('inApp')) return t('eventNotification.inApp.channelName')
    return ''
  })

  const blockerLabel = (blocker: Blocker): string => {
    switch (blocker) {
      case 'no-agent':
        return t('eventNotification.voiceAi.blockers.noAgent')
      case 'no-number':
        return t('eventNotification.voiceAi.blockers.noNumber')
      default:
        return t('eventNotification.voiceAi.blockers.notEnabled')
    }
  }

  async function createReminderAgent() {
    if (creatingAgent.value) return
    creatingAgent.value = true
    try {
      const { data } = await VoiceAiReminderService.createReminderAgent(AppState.locationId, props.calendarId, calendarName.value)
      agentOptions.value = [{ label: data.name, value: data.id }, ...agentOptions.value]
      form.agentId = data.id
      notification.success({ content: t('eventNotification.voiceAi.agentCreated'), duration: 6000 })
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status
      if (status === 403) {
        createAgentForbidden.value = true
        notification.error({ content: t('eventNotification.voiceAi.agentCreateNoPermission'), duration: 6000 })
      } else {
        console.error('Voice AI reminder agent creation failed', error)
        notification.error({ content: t('eventNotification.voiceAi.agentCreateFailed'), duration: 6000 })
      }
    } finally {
      creatingAgent.value = false
    }
  }

  function openSelectedAgent() {
    if (!form.agentId) return

    window.open(getVoiceAgentUrl(AppState.locationId, form.agentId), '_blank')?.focus()
  }

  /** A call we cannot confirm is a call we should not offer, so any failure reads as not ready. */
  async function checkReadiness() {
    checking.value = true
    const missing: Blocker[] = []
    try {
      const [agents, consent, numbers] = await Promise.all([
        VoiceAiReminderService.getAgents(AppState.locationId),
        VoiceAiReminderService.getOutboundConsent(AppState.locationId),
        VoiceAiReminderService.getLocationNumbers(AppState.locationId),
      ])
      agentOptions.value = (agents.data || []).filter(agent => !agent.isDeleted).map(agent => ({ label: agent.name, value: agent.id }))
      numberOptions.value = (numbers.data?.numbers || []).map(number => ({
        label: number.friendlyName ? `${number.phoneNumber} — ${number.friendlyName}` : number.phoneNumber,
        value: number.phoneNumber,
      }))
      if (!agentOptions.value.length && !canCreateAgent.value) missing.push('no-agent')
      if (!numberOptions.value.length) missing.push('no-number')
      if (!consent.data?.consentGiven) consentState.value = 'required'
      else if (!consent.data.selfCertifiedConsentGiven) consentState.value = 'upgrade'
      else consentState.value = 'accepted'
    } catch (error) {
      console.error('Voice AI readiness check failed', error)
      missing.push('not-enabled')
    } finally {
      blockers.value = missing
      checking.value = false
    }
  }

  function hydrateFromSavedRows() {
    const row = (state.voiceAiNotifications || []).find(entry => entry.receiverType === ReceiverType.CONTACT)
    if (!row) return
    if (row._id) rowId.value = row._id
    enabled.value = row.isActive && !row.deleted
    form.agentId = row.voiceAgentId || ''
    form.fromNumber = row.fromNumber || ''
    const saved = row.beforeTime?.[0]
    if (saved) form.interval = { timeOffset: Number(saved.timeOffset) || 0, unit: saved.unit }
  }

  /** The reminder call goes to the contact on the appointment only, so one row carries the whole configuration. */
  function buildRow(): NotificationConfig {
    return {
      _id: rowId.value,
      altType: AltType.CALENDAR,
      altId: props.calendarId,
      channel: NotificationChannel.VOICE_AI,
      notificationType: NotificationType.REMINDER,
      receiverType: ReceiverType.CONTACT,
      templateId: '',
      body: '',
      subject: '',
      voiceAgentId: form.agentId,
      fromNumber: form.fromNumber,
      beforeTime: [{ timeOffset: Number(form.interval.timeOffset), unit: form.interval.unit }],
      afterTime: [],
      selectedUsers: [],
      isActive: enabled.value,
      deleted: !enabled.value,
    }
  }

  /** Resolves with an error message when the settings can't be saved as they are. */
  function validateAll(): string | null {
    if (!enabled.value) return null
    if (!form.agentId) return t('eventNotification.voiceAi.agentRequired')
    if (!form.fromNumber) return t('eventNotification.voiceAi.fromNumberRequired')
    if (offsetErrorMessage.value) return offsetErrorMessage.value
    if (consentState.value === 'required' && !form.termsAgreed) return t('eventNotification.voiceAi.consent.required')
    return null
  }

  // Unlike the other tabs, switching Voice AI on does not save immediately: an enabled channel with
  // no agent or number would be persisted as a reminder that can never place a call.
  async function save(): Promise<boolean> {
    const row = buildRow()
    try {
      // Automated calls need the account's acceptance of the calling terms on record first.
      if (enabled.value && consentState.value !== 'accepted' && form.termsAgreed) {
        await VoiceAiReminderService.acceptOutboundTerms(AppState.locationId)
        consentState.value = 'accepted'
      }
      if (row._id && !isVirtualNotification(row._id)) {
        await EventNotificationService.updateEventNotifications([row])
      } else {
        const { data } = await EventNotificationService.createEventNotifications([stripNotificationIdForCreate(row)])
        const created = Array.isArray(data) ? data[0] : data
        if (created?._id) rowId.value = created._id
      }
    } catch (error) {
      console.error('Voice AI notification save failed', error)
      return false
    }
    emit('update-notification', {
      channel: NotificationChannel.VOICE_AI,
      type: NotificationType.REMINDER,
      status: enabled.value,
    })
    return true
  }

  defineExpose({ validateAll, save })

  onMounted(async () => {
    hydrateFromSavedRows()
    await checkReadiness()
  })
</script>

<style scoped>
  .disabled-div {
    position: relative;
    pointer-events: none;
    user-select: none;
    opacity: 0.5;
  }
</style>
